import React, {
  type CSSProperties,
  type ElementType,
  type PropsWithChildren,
  forwardRef,
} from 'react'

import { css, cx, type LinariaClassName } from '@linaria/core'
import { styled } from '@linaria/react'
import type { ColorValue } from '../styles/colors'

type Fonts = 'manrope' | 'roboto'

export const Typography = forwardRef<
  HTMLHeadingElement,
  {
    fontSize: FontSize
    fontWeight?: FontWeight
    tag?: ElementType
    style?: CSSProperties
    className?: string
    font?: Fonts
    color?: ColorValue
  } & PropsWithChildren
>(
  (
    {
      className,
      fontSize,
      fontWeight = 400,
      children,
      tag,
      style,
      font = 'manrope',
      color,
    },
    ref,
  ) => {
    return (
      <InlineText
        ref={ref}
        style={style}
        className={cx(
          typographyMapCss[fontSize],
          fontFamilyMapCss[font],
          fontWeightMapCss[fontWeight],
          className,
        )}
        as={tag}
        color={color}
      >
        {children}
      </InlineText>
    )
  },
)
Typography.displayName = 'Typography'

export type FontSize = 28 | 24 | 20 | 16 | 14 | 13 | 12 | 10 | 9 | 8
export type FontWeight = 400 | 500 | 600 | 700 | 900

// Font size constants for use in styled components (CSS-in-JS)
export const fontSize: Record<FontSize, string> = {
  28: '28px',
  24: '24px',
  20: '20px',
  16: '16px',
  14: '14px',
  13: '13px',
  12: '12px',
  10: '10px',
  9: '9px',
  8: '8px',
} as const

const InlineText = styled.span<{ color?: string }>`
  word-break: break-word;
  color: ${props => props.color || 'var(--color-text-primary)'};
`
export const typographyMapCss: Record<FontSize, LinariaClassName> = {
  28: css`
    font-size: 28px;
    font-weight: 600;
    line-height: 32px;
  `,
  24: css`
    font-size: 24px;
    font-weight: 600;
    line-height: 28px;
  `,
  20: css`
    font-size: 20px;
    font-weight: 600;
    line-height: 24px;
  `,
  16: css`
    font-size: 16px;
    font-weight: 400;
    line-height: 20px;
  `,
  14: css`
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
  `,
  13: css`
    font-size: 13px;
    font-weight: 400;
    line-height: 16px;
  `,
  12: css`
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
  `,
  10: css`
    font-size: 10px;
    font-weight: 400;
    line-height: 14px;
  `,
  9: css`
    font-size: 9px;
    font-weight: 400;
    line-height: 12px;
  `,
  8: css`
    font-size: 8px;
    font-weight: 400;
    line-height: 10px;
  `,
}
export const fontWeightMapCss: Record<FontWeight, LinariaClassName> = {
  400: css`
    font-weight: 400;
  `,
  500: css`
    font-weight: 500;
  `,
  600: css`
    font-weight: 600;
  `,
  700: css`
    font-weight: 700;
  `,
  900: css`
    font-weight: 900;
  `,
}
const manropeFontCss = css`
  font-family: 'Manrope', sans-serif;
  font-optical-sizing: auto;
  font-style: normal;
`
const robotoFontCss = css`
  font-family: 'Roboto', sans-serif;
  font-optical-sizing: auto;
  font-style: normal;
  font-variation-settings: 'wdth' 100;
`
export const fontFamilyMapCss: Record<Fonts, LinariaClassName> = {
  manrope: manropeFontCss,
  roboto: robotoFontCss,
}
